import { useState, useEffect, useCallback } from 'react';
import { auth } from '@/lib/picasso-firebase';
import { signOut } from 'firebase/auth';
import { PendingUser, ApprovedUser } from '@/schemas/admin.types';
import { UserService } from '@/hooks/admin/userService';
import { useLogout } from '@/hooks/auth/useLogout';

interface UseAdminDashboardReturn {
  // Data
  pendingUsers: PendingUser[];
  approvedUsers: ApprovedUser[];
  currentAdmin: string;
  error: string | null;
  deletingUserId: string | null;
  isDataLoading: boolean;
  
  // Actions
  approveUser: (user: PendingUser) => Promise<void>;
  rejectUser: (user: PendingUser) => Promise<void>;
  deleteUser: (user_id: string) => Promise<void>;
  handleLogout: () => Promise<void>;
  refreshData: () => Promise<void>;
  clearError: () => void;
}

export const useAdminDashboard = (): UseAdminDashboardReturn => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<ApprovedUser[]>([]);
  const [currentAdmin, setCurrentAdmin] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);

  const userService = UserService.getInstance();
  const { logout } = useLogout();

  // Initialize admin user
  useEffect(() => {
    const initializeAdmin = () => {
      if (auth.currentUser?.email) {
        setCurrentAdmin(auth.currentUser.email);
      } else {
        // Wait for auth to initialize
        const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user?.email) {
            setCurrentAdmin(user.email);
          }
        });
        return unsubscribe;
      }
    };

    const cleanup = initializeAdmin();
    return cleanup;
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setIsDataLoading(true);
      setError(null);
      
      const [pending, approved] = await Promise.all([
        userService.fetchPendingUsers(),
        userService.fetchApprovedUsers()
      ]);
      
      setPendingUsers(pending);
      setApprovedUsers(approved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setIsDataLoading(false);
    }
  }, [userService]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const approveUser = useCallback(async (user: PendingUser) => {
    try {
      setError(null);
      const adminEmail = auth.currentUser?.email || currentAdmin;
      
      await userService.approveUser(user, adminEmail);
      
      // Update local state instead of refetching all data
      setPendingUsers(prev => prev.filter(u => u.id !== user.id));
      setApprovedUsers(prev => [...prev, {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        created_at: user.created_at,
        status: 'approved' as const,
        approved_by: adminEmail,
        approved_at: new Date().toISOString()
      }]);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve user';
      setError(errorMessage);
      alert(errorMessage);
    }
  }, [userService, currentAdmin]);

  const rejectUser = useCallback(async (user: PendingUser) => {
    try {
      setError(null);
      await userService.rejectUser(user);
      
      // Update local state instead of refetching all data
      setPendingUsers(prev => prev.filter(u => u.id !== user.id));
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reject user';
      setError(errorMessage);
      alert(errorMessage);
    }
  }, [userService]);

  const deleteUser = useCallback(async (userId: string) => {
    try {
      setError(null);
      setDeletingUserId(userId);
      
      await userService.deleteUser(userId);
      
      // Update local state immediately instead of refetching
      setApprovedUsers(prev => prev.filter(u => u.id !== userId));
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setDeletingUserId(null);
    }
  }, [userService]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err) {
      // Fallback to simple Firebase logout if backend logout fails
      console.error('Backend logout failed, falling back to Firebase logout:', err);
      try {
        await signOut(auth);
        globalThis.location.assign('/login');
      } catch (error_) {
        // Silent fallback failure
        console.error('Logout failed:', error_);
      }
    }
  }, [logout]);

  const refreshData = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    pendingUsers,
    approvedUsers,
    currentAdmin,
    error,
    deletingUserId,
    isDataLoading,
    approveUser,
    rejectUser,
    deleteUser,
    handleLogout,
    refreshData,
    clearError
  };
};